import { useEffect, useRef, useState } from 'react';
import { AtendentesService } from '../../../atendentes/Service';
import { initials, LoadFailure } from '../../booking/BookingParts';
import { baseURL } from '../../../../AxiosConfig';
import { useEstabsContext } from '../../../../contexts/EstabsContext';
import { useFormHorarioClienteContext } from './Context';

const serviceAtendentes = new AtendentesService();

// Fica montado mesmo fora da etapa: preenche o profissional da edicao, do filtro ou o unico disponivel.
export const SelectAtendente = ({ visible = true }: { visible?: boolean }) => {
    const { estabSession } = useEstabsContext();
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [atendentes, setAtendentes] = useState([] as any[]);
    const hydratedFromDocRef = useRef(false);

    const {
        atendente,
        setAtendente,
        setServico,
        setServicosExtras,
        setHorario,
        setShowHorarios,
        doc,
        lockAtendente,
        lockServico,
        setAttendantCount,
    } = useFormHorarioClienteContext();

    const serviceFilterId = lockServico ? doc?.servico_id : undefined;

    const resetSelectionAfterAttendantChange = () => {
        setServico({} as any);
        setServicosExtras([]);
        setHorario(0);
        setShowHorarios(false);
    };

    const loadAtendentes = async () => {
        if (!estabSession?._id) {
            setAtendentes([]);
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            setLoadError(false);
            const { data } = await serviceAtendentes.getByEstabId(estabSession._id, serviceFilterId);
            const list = Array.isArray(data) ? data : [];
            setAtendentes(list);
            setAttendantCount(list.length);
        } catch (error) {
            setAtendentes([]);
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadAtendentes();
    }, [estabSession?._id, serviceFilterId]);

    useEffect(() => {
        hydratedFromDocRef.current = false;
    }, [doc?._id, doc?.atendente_id]);

    useEffect(() => {
        if (loading) return;
        if (hydratedFromDocRef.current) return;

        const docAttendantId = String(doc?.atendente_id || '').trim();
        if (!docAttendantId) {
            hydratedFromDocRef.current = true;
            return;
        }

        if (!atendentes.length) return;

        const fromDoc = atendentes.find((item: any) => item?.uid === docAttendantId);
        if (fromDoc) {
            setAtendente(fromDoc);
        }
        hydratedFromDocRef.current = true;
    }, [loading, atendentes, doc?.atendente_id, setAtendente]);

    useEffect(() => {
        if (loading) return;

        if (atendentes.length === 1) {
            const onlyAttendant = atendentes[0];
            if (atendente?.uid !== onlyAttendant?.uid) {
                setAtendente(onlyAttendant);
            }
            return;
        }

        if (atendente?.uid && !atendentes.some((item: any) => item?.uid === atendente.uid)) {
            setAtendente({} as any);
            resetSelectionAfterAttendantChange();
        }
    }, [atendentes, loading, atendente?.uid, setAtendente]);

    const handleToggleAtendente = (selected: any) => {
        if (lockAtendente) return;

        const isCurrent = selected?.uid === atendente?.uid;
        if (isCurrent) {
            setAtendente({} as any);
            resetSelectionAfterAttendantChange();
            return;
        }

        setAtendente(selected);
        resetSelectionAfterAttendantChange();
    };

    if (!visible) return null;

    if (loading) {
        return (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3" role="status" aria-label="Carregando profissionais">
                {[0, 1, 2, 3].map((item) => <div key={item} className="sf-skeleton h-36 rounded-2xl" />)}
            </div>
        );
    }

    if (loadError) {
        return <LoadFailure message="Não foi possível carregar os profissionais." onRetry={loadAtendentes} />;
    }

    if (!atendentes.length) {
        return <p className="py-10 text-center text-sm text-base-content/70">Nenhum profissional disponível.</p>;
    }

    return (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3">
            {atendentes.map((item: any) => (
                <li key={item?.uid}>
                    <button
                        type="button"
                        disabled={lockAtendente}
                        className="flex h-full w-full flex-col items-center gap-3 rounded-2xl bg-base-100 px-3 py-5 ring-1 ring-base-content/[0.06] transition-colors hover:bg-base-100/70 active:bg-base-content/5 disabled:cursor-not-allowed disabled:opacity-60"
                        onClick={() => handleToggleAtendente(item)}
                    >
                        <span className="relative flex h-20 w-20 items-center justify-center overflow-hidden rounded-full bg-base-200 text-xl font-semibold text-base-content/40">
                            {initials(item?.displayName)}
                            <img
                                src={`${baseURL}/users/profileImage/${item?.uid}`}
                                alt=""
                                className="absolute inset-0 h-full w-full object-cover"
                                onError={(event) => { event.currentTarget.hidden = true; }}
                            />
                        </span>
                        <span className="break-words text-center text-[0.9375rem] font-medium text-base-content">{item?.displayName}</span>
                    </button>
                </li>
            ))}
        </ul>
    );
};
